import { useEffect, useState, useMemo } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import { previewPdfResponse } from "../lib/pdfPreview";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
    ShieldCheck,
    ShieldOff,
    Shield,
    ShieldAlert,
    ShieldQuestion,
    FileUp,
    ArrowLeft,
    Clock,
    Mail,
    Building2,
    AlertCircle,
    Loader2,
    Download,
    FileText as FileTextIcon,
    FileSpreadsheet,
} from "lucide-react";

/**
 * Per-dealer verification audit timeline. Shows every approve / reject /
 * verify / unverify event (admin actions) plus every dealer self-service
 * action (verification_requested / resubmitted) plus the synthetic
 * "document_replaced" entries derived from business_doc_history.
 */
const EVENT_META = {
    dealer_approved:                  { icon: ShieldCheck,    tone: "green",  testid: "ev-approved" },
    dealer_verified:                  { icon: ShieldCheck,    tone: "green",  testid: "ev-verified" },
    dealer_rejected:                  { icon: ShieldOff,      tone: "red",    testid: "ev-rejected" },
    dealer_unverified:                { icon: Shield,         tone: "gray",   testid: "ev-unverified" },
    dealer_verification_requested:    { icon: ShieldAlert,    tone: "blue",   testid: "ev-requested" },
    dealer_verification_resubmitted:  { icon: ShieldAlert,    tone: "amber",  testid: "ev-resubmitted" },
    document_replaced:                { icon: FileUp,         tone: "amber",  testid: "ev-doc" },
};

const TONE_CLASSES = {
    green: "bg-green-50 border-green-300 text-green-900",
    red: "bg-red-50 border-red-300 text-red-900",
    gray: "bg-gray-50 border-gray-300 text-gray-800",
    blue: "bg-blue-50 border-blue-300 text-blue-900",
    amber: "bg-amber-50 border-amber-300 text-amber-900",
};

const ICON_CLASSES = {
    green: "bg-green-600 text-white",
    red: "bg-red-600 text-white",
    gray: "bg-gray-500 text-white",
    blue: "bg-blue-600 text-white",
    amber: "bg-amber-500 text-white",
};

export default function AdminDealerAuditPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user, loading } = useAuth();
    const { t, i18n } = useTranslation();
    const [data, setData] = useState(null);
    const [fetching, setFetching] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    const [searchParams, setSearchParams] = useSearchParams();
    // ``?cat=`` URL param drives the filter chip selection so deep-links are
    // shareable (e.g. ``/admin/dealers/{id}/audit?cat=reject``).
    const VALID_CATS = useMemo(() => new Set(["all", "approve", "reject", "self", "docs"]), []);
    const urlCat = searchParams.get("cat");
    const filterCat = VALID_CATS.has(urlCat) ? urlCat : "all";
    const setFilterCat = (cat) => {
        const next = new URLSearchParams(searchParams);
        if (cat === "all") next.delete("cat");
        else next.set("cat", cat);
        setSearchParams(next, { replace: true });
    };
    const [exporting, setExporting] = useState(null); // 'csv' | 'pdf' | null
    const dateLocale = i18n.resolvedLanguage === "en" ? "en-GB" : "de-DE";

    useEffect(() => {
        if (loading) return;
        if (!user || user.role !== "admin") return;
        let cancelled = false;
        setFetching(true);
        api.get(`/admin/dealers/${id}/audit?limit=100`)
            .then((r) => { if (!cancelled) setData(r.data); })
            .catch((e) => { if (!cancelled) setError(e.response?.data?.detail || e.message); })
            .finally(() => { if (!cancelled) setFetching(false); });
        return () => { cancelled = true; };
    }, [id, user, loading]);

    const loadMore = async () => {
        if (!data?.next_cursor || loadingMore) return;
        setLoadingMore(true);
        try {
            const r = await api.get(`/admin/dealers/${id}/audit?limit=100&cursor=${encodeURIComponent(data.next_cursor)}`);
            // Prepend older events so chronological order is preserved.
            setData((prev) => ({
                ...prev,
                events: [...r.data.events, ...prev.events],
                next_cursor: r.data.next_cursor,
                has_more: r.data.has_more,
            }));
        } catch (e) {
            // Silent failure — user can re-click.
        } finally {
            setLoadingMore(false);
        }
    };

    const exportAudit = async (format) => {
        setExporting(format);
        try {
            const r = await api.get(`/admin/dealers/${id}/audit/export?format=${format}`, {
                responseType: "blob",
            });
            const cd = r.headers["content-disposition"] || "";
            const m = /filename="([^"]+)"/.exec(cd);
            const filename = m ? m[1] : `audit.${format}`;
            if (format === "pdf") {
                previewPdfResponse(r, filename);
            } else {
                // CSV → direkter Datei-Download.
                const url = URL.createObjectURL(r.data);
                const a = document.createElement("a");
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
            }
        } catch (e) {
            // Best-effort UI; surface in console only.
            // eslint-disable-next-line no-console
            console.error("Audit export failed", e);
        } finally {
            setExporting(null);
        }
    };

    if (loading || fetching) {
        return <div className="p-12" data-testid="audit-loading">{t("admin_dealer_audit.loading")}</div>;
    }
    if (!user || user.role !== "admin") {
        return (
            <div className="max-w-3xl mx-auto px-4 py-16 text-center" data-testid="audit-forbidden">
                <h1 className="font-display text-3xl font-black mb-2">{t("admin_dealers.forbidden_title")}</h1>
                <p className="text-gray-600">{t("admin_dealers.forbidden_sub")}</p>
                <Link to="/" className="inline-block mt-6 text-sm font-bold underline">{t("admin_dealers.forbidden_home")}</Link>
            </div>
        );
    }
    if (error) {
        return (
            <div className="max-w-3xl mx-auto px-4 py-16 text-center" data-testid="audit-error">
                <AlertCircle className="w-10 h-10 mx-auto text-red-500 mb-4" />
                <h1 className="font-display text-2xl font-black mb-2">{t("admin_dealer_audit.error_title")}</h1>
                <p className="text-gray-600">{error}</p>
                <Button onClick={() => navigate("/admin/dealers")} className="mt-6 rounded-sm bg-[#111827] text-white">
                    <ArrowLeft className="w-4 h-4 mr-2" /> {t("admin_dealer_audit.back_to_list")}
                </Button>
            </div>
        );
    }

    const dealer = data?.dealer;
    const allEvents = data?.events || [];
    const statusMeta = STATUS_META[dealer?.verification_status || "none"];
    const StatusIcon = statusMeta.icon;
    // Filter chips appear when the dealer's *total* timeline exceeds 100
    // events. We use the server-side total (not just the currently-loaded
    // page) so the bar shows up immediately on the first page load instead
    // of waiting for the user to "Load more" first.
    const showFilter = (data?.total || 0) > 100;

    const filterEvents = (events, cat) => {
        if (cat === "all") return events;
        if (cat === "approve") return events.filter((e) => ["dealer_approved", "dealer_verified"].includes(e.event_type));
        if (cat === "reject") return events.filter((e) => ["dealer_rejected", "dealer_unverified"].includes(e.event_type));
        if (cat === "self") return events.filter((e) => ["dealer_verification_requested", "dealer_verification_resubmitted"].includes(e.event_type));
        if (cat === "docs") return events.filter((e) => e.event_type === "document_replaced");
        return events;
    };
    const events = filterEvents(allEvents, filterCat);

    return (
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-8 md:py-12" data-testid="admin-dealer-audit-page">
            {/* Back link */}
            <Link
                to={`/admin/dealers?status=${dealer.verification_status || "all"}`}
                className="inline-flex items-center gap-1 text-sm font-bold uppercase tracking-wider text-gray-600 hover:text-[#111827] mb-4"
                data-testid="audit-back"
            >
                <ArrowLeft className="w-4 h-4" /> {t("admin_dealer_audit.back_to_list")}
            </Link>

            {/* Dealer header card */}
            <div className="bg-white border border-gray-200 rounded-sm p-6 mb-8" data-testid="audit-dealer-card">
                <div className="text-xs font-bold uppercase tracking-wider text-[#FFCC00] mb-2">{t("admin_dealer_audit.kicker")}</div>
                <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight flex items-center gap-3">
                    <Building2 className="w-7 h-7 text-gray-400" />
                    {dealer.company_name || dealer.name || dealer.email}
                </h1>
                <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
                    <Badge className={`${statusMeta.badgeClass} hover:${statusMeta.badgeClass} gap-1 rounded-sm`} data-testid="audit-status-badge">
                        <StatusIcon className="w-3 h-3" /> {t(`admin_dealers.status_${dealer.verification_status || "none"}`)}
                    </Badge>
                    <span className="inline-flex items-center gap-1 text-gray-600">
                        <Mail className="w-3 h-3" /> <span className="font-mono-num">{dealer.email}</span>
                    </span>
                    {dealer.business_doc_url && (
                        <a
                            href={dealer.business_doc_url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-blue-600 hover:underline text-xs font-bold uppercase tracking-wider"
                            data-testid="audit-current-doc"
                        >
                            {t("admin_dealers.view_doc_link")}
                        </a>
                    )}
                </div>
                {dealer.verification_status === "rejected" && dealer.verification_note && (
                    <div className="mt-4 bg-red-50 border-l-4 border-red-500 p-3 rounded-sm text-sm text-red-900" data-testid="audit-current-note">
                        <div className="text-[11px] font-bold uppercase tracking-wider mb-1">{t("admin_dealer_audit.current_note")}</div>
                        <div className="italic">„{dealer.verification_note}"</div>
                    </div>
                )}

                {/* Compliance export — single endpoint, two formats. */}
                <div className="mt-5 pt-4 border-t border-gray-100 flex flex-wrap gap-2 items-center" data-testid="audit-export-bar">
                    <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mr-2">
                        {t("admin_dealer_audit.export_label")}
                    </div>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => exportAudit("csv")}
                        disabled={exporting !== null}
                        className="rounded-sm text-xs font-bold uppercase tracking-wider"
                        data-testid="audit-export-csv"
                    >
                        {exporting === "csv"
                            ? <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
                            : <FileSpreadsheet className="w-3 h-3 mr-1.5" />}
                        CSV
                    </Button>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => exportAudit("pdf")}
                        disabled={exporting !== null}
                        className="rounded-sm text-xs font-bold uppercase tracking-wider"
                        data-testid="audit-export-pdf"
                    >
                        {exporting === "pdf"
                            ? <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
                            : <FileTextIcon className="w-3 h-3 mr-1.5" />}
                        PDF
                    </Button>
                    <span className="text-[11px] text-gray-500 ml-auto">
                        {t("admin_dealer_audit.export_hint")}
                    </span>
                </div>
            </div>

            {/* Timeline */}
            <div className="mb-6 flex items-baseline justify-between flex-wrap gap-3">
                <h2 className="font-display text-xl font-black">{t("admin_dealer_audit.timeline_title")}</h2>
                <span className="text-xs text-gray-500 font-mono-num" data-testid="audit-total-count">
                    {filterCat !== "all"
                        ? t("admin_dealer_audit.event_count_filtered", { shown: events.length, total: allEvents.length })
                        : (data?.total && allEvents.length < data.total)
                            ? t("admin_dealer_audit.event_count_paginated", { loaded: allEvents.length, total: data.total })
                            : t("admin_dealer_audit.event_count", { count: events.length })}
                </span>
            </div>

            {/* "Mehr laden" affordance — only shown when the dealer has more
                than 100 historic events (server set has_more=true) AND the
                user isn't currently filtering. */}
            {data?.has_more && filterCat === "all" && (
                <div className="mb-4 text-center" data-testid="audit-load-more-wrapper">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={loadMore}
                        disabled={loadingMore}
                        className="h-10 rounded-sm font-bold text-xs uppercase tracking-wider"
                        data-testid="audit-load-more"
                    >
                        {loadingMore
                            ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            : <Clock className="w-4 h-4 mr-2" />}
                        {t("admin_dealer_audit.load_more")}
                    </Button>
                </div>
            )}

            {showFilter && (
                <div className="mb-4 flex flex-wrap gap-2" data-testid="audit-filter-bar">
                    {[
                        { v: "all",     labelKey: "filter_all" },
                        { v: "approve", labelKey: "filter_approvals" },
                        { v: "reject",  labelKey: "filter_rejections" },
                        { v: "self",    labelKey: "filter_self_service" },
                        { v: "docs",    labelKey: "filter_documents" },
                    ].map((opt) => (
                        <Button
                            key={opt.v}
                            type="button"
                            onClick={() => setFilterCat(opt.v)}
                            variant={filterCat === opt.v ? "default" : "outline"}
                            className={`h-9 rounded-sm font-bold text-xs uppercase tracking-wider ${filterCat === opt.v ? "bg-[#111827] text-white" : ""}`}
                            data-testid={`audit-filter-${opt.v}`}
                        >
                            {t(`admin_dealer_audit.${opt.labelKey}`)}
                        </Button>
                    ))}
                </div>
            )}

            {events.length === 0 ? (
                <div className="bg-gray-50 border border-dashed border-gray-300 rounded-sm p-12 text-center text-gray-500" data-testid="audit-empty">
                    <Clock className="w-10 h-10 mx-auto mb-4 text-gray-400" />
                    <p>{filterCat === "all" ? t("admin_dealer_audit.empty") : t("admin_dealer_audit.empty_filtered")}</p>
                </div>
            ) : (
                <ol className="relative space-y-4 border-l-2 border-gray-200 pl-6 ml-3" data-testid="audit-timeline">
                    {events.map((ev, idx) => {
                        const meta = EVENT_META[ev.event_type] || { icon: AlertCircle, tone: "gray", testid: "ev-unknown" };
                        const Icon = meta.icon;
                        const tone = TONE_CLASSES[meta.tone];
                        const iconBg = ICON_CLASSES[meta.tone];
                        const note = ev.payload?.note;
                        const prevNote = ev.payload?.previous_note;
                        const replacedUrl = ev.payload?.replaced_url;
                        return (
                            <li key={ev.id || idx} className="relative" data-testid={`audit-event-${idx}`}>
                                <span className={`absolute -left-[37px] top-0 w-7 h-7 rounded-full flex items-center justify-center ${iconBg}`}>
                                    <Icon className="w-3.5 h-3.5" />
                                </span>
                                <div className={`border-l-4 rounded-sm p-3 ${tone}`} data-testid={meta.testid}>
                                    <div className="flex items-baseline justify-between flex-wrap gap-2">
                                        <div className="font-bold text-sm">
                                            {t(`admin_dealer_audit.event_${ev.event_type}`, { defaultValue: ev.event_type })}
                                        </div>
                                        <time className="text-[11px] text-gray-500 font-mono-num">
                                            {new Date(ev.created_at).toLocaleString(dateLocale, { dateStyle: "medium", timeStyle: "short" })}
                                        </time>
                                    </div>
                                    <div className="mt-1 text-xs text-gray-700">
                                        <span className="font-bold">{t("admin_dealer_audit.by_actor")}: </span>
                                        <span className="font-mono-num">{ev.actor_label || ev.actor_id || "—"}</span>
                                    </div>
                                    {note && (
                                        <div className="mt-2 italic text-sm border-l-2 border-current/40 pl-3" data-testid={`audit-note-${idx}`}>
                                            „{note}"
                                        </div>
                                    )}
                                    {prevNote && ev.event_type === "dealer_verification_resubmitted" && (
                                        <div className="mt-2 text-[11px] text-gray-600">
                                            {t("admin_dealer_audit.replaced_after_note")}: <span className="italic">„{prevNote}"</span>
                                        </div>
                                    )}
                                    {replacedUrl && (
                                        <a
                                            href={replacedUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="mt-2 inline-block text-xs text-blue-700 hover:underline font-mono-num"
                                            data-testid={`audit-replaced-link-${idx}`}
                                        >
                                            {t("admin_dealer_audit.view_replaced_doc")} →
                                        </a>
                                    )}
                                </div>
                            </li>
                        );
                    })}
                </ol>
            )}
        </div>
    );
}

const STATUS_META = {
    approved: { icon: ShieldCheck,    badgeClass: "bg-green-100 text-green-800" },
    pending:  { icon: Clock,          badgeClass: "bg-amber-100 text-amber-800" },
    rejected: { icon: ShieldOff,      badgeClass: "bg-red-100 text-red-800" },
    none:     { icon: ShieldQuestion, badgeClass: "bg-gray-200 text-gray-700" },
};
